import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectApp,
    selectRelease,
    selectAppPlatform,
    selectAppStoreType,
    selectIsDemoInstance,
    selectIsWorkflowsCIStepEnabled,
    selectTimelineEventsFiltersMap,
    selectShouldShowAutomationsTimelineEventsOnly,
    selectIsStepTimelineDrawerVisible,
} from '../../selectors/selectors';
import { Release, ReleaseStepType, TimelineEvent } from '../../types';
import { ReleaseTimelineSectionContext } from '../timelineEvents/EventsTimeline';
import { globalMessage } from '../../utils/globalMessage';
import { EventsTimeline } from '../timelineEvents/EventsTimeline';
import { EventsTimelineDrawer } from '../timelineEvents/EventsTimelineDrawer';
import { displayTextForReleaseStepType } from '../../helpers/displayHelpers';
import { updateTimelineEventsFilters } from '../../actions/filters';
import { useTimelineEventsPagination } from '@/utils/hooks/useTimelineEventsPagination';
import { filterTimelineEvent } from '@/types/timelineEventsFilters';
import { setEventsTimelineDrawerVisibility } from '../../actions/ui';

export interface EventsTimelineSectionProps {
    events?: TimelineEvent[];
    maxItemsToDisplay?: number;
    orgId: string;
    appId: string;
    releaseId: string;
    releaseStepType?: ReleaseStepType;
    context: ReleaseTimelineSectionContext;
    isLoading?: boolean;
    stepRunId?: string;
    loadMore?: () => void;
    isLoadingMore?: boolean;
    hasMore?: boolean;
}

export const EventsTimelineSection: React.FC<EventsTimelineSectionProps> = ({
    events,
    maxItemsToDisplay,
    orgId,
    appId,
    releaseId,
    releaseStepType,
    isLoading,
    context,
    loadMore,
    isLoadingMore,
    hasMore,
    stepRunId,
}) => {
    const dispatch = useDispatch();

    const stepId = stepRunId ?? releaseStepType;
    const isDrawerVisible = useSelector((state: RootState) => selectIsStepTimelineDrawerVisible(state, stepId));

    const release = useSelector((state: RootState) => selectRelease(state, releaseId)) as Release;

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    return (
        <>
            {releaseStepType != ReleaseStepType.Rollout && (
                <EventsTimeline
                    isLoading={isLoading}
                    events={events}
                    maxItemsToDisplay={maxItemsToDisplay ?? 3}
                    orgId={orgId}
                    appId={appId}
                    release={release}
                    releaseType={release.type}
                    shouldHideUpcomingEvents={context === ReleaseTimelineSectionContext.AppHome}
                    releaseStepType={releaseStepType}
                    context={context}
                />
            )}
            <EventsTimelineDrawer
                events={events}
                open={isDrawerVisible}
                onClose={() => {
                    dispatch(setEventsTimelineDrawerVisibility(null, false));
                    dispatch(updateTimelineEventsFilters({ filters: [] }));
                }}
                orgId={orgId}
                appId={appId}
                version={release.releaseId}
                releaseStepDisplayName={displayTextForReleaseStepType(
                    releaseStepType,
                    platform,
                    appStoreType,
                    isWorkflowsCIStepEnabled,
                )}
                releaseStepType={releaseStepType}
                loadMore={loadMore}
                isLoading={isLoadingMore || isLoading}
                hasMore={hasMore}
            />
        </>
    );
};

type ReleaseEventsTimelineSectionProps = Omit<EventsTimelineSectionProps, 'events' | 'isLoading'>;

export const ReleaseEventsTimelineSection: React.FC<ReleaseEventsTimelineSectionProps> = (props) => {
    const { appId, releaseId, orgId, stepRunId, releaseStepType } = props;

    const workflowRunId = useSelector((state: RootState) => {
        const release = selectRelease(state, releaseId);
        return release.workflowRunId;
    });

    const workflowId = useSelector((state: RootState) => {
        const app = selectApp(state, appId);
        return app.workflowId;
    });

    const isDemoInstance = orgId ? useSelector((state: RootState) => selectIsDemoInstance(state, props.orgId)) : false;
    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId));

    const { events, isLoading, error, loadMore, isLoadingMore, paginationInfo, refresh } =
        isDemoInstance ?
            {
                events: release?.timelineEventsHistory || [],
                isLoading: false,
                error: false,
                loadMore: () => {},
                isLoadingMore: false,
                paginationInfo: {},
                refresh: () => {},
            }
        :   useTimelineEventsPagination({
                orgId,
                appId,
                releaseId,
                releaseStepType,
                workflowId,
                workflowRunId,
                stepRunIds: stepRunId ? [stepRunId] : [],
                initialLoad: true,
            });

    const currentFiltersMap = useSelector((state: RootState) => selectTimelineEventsFiltersMap(state));
    const shouldShowAutomationsOnly = useSelector((state: RootState) =>
        selectShouldShowAutomationsTimelineEventsOnly(state),
    );
    const filteredEvents =
        events?.filter((event) => filterTimelineEvent(event, currentFiltersMap, shouldShowAutomationsOnly)) || [];

    React.useEffect(() => {
        if (error) {
            globalMessage.error('An error occurred when fetching release timeline events.');
        }
    }, [error]);

    React.useEffect(() => {
        if (release?.RenderedAt) {
            refresh();
        }
    }, [release?.RenderedAt]);

    return (
        <EventsTimelineSection
            {...props}
            loadMore={loadMore}
            isLoadingMore={isLoadingMore}
            hasMore={paginationInfo?.hasMore}
            events={filteredEvents}
            isLoading={isLoading}
            key={`${releaseId}-${stepRunId}-${releaseStepType}`}
        />
    );
};
